import type { EntitlementKey } from '../api/entitlements';
import type { PermissionKey } from './permissions';
import { routePermissionFor } from './permissionGates';
import { routeRoleFor } from './roleGates';

// ---------------------------------------------------------------------------
// The one table of which pages a plan unlocks. The desktop rail, the mobile
// drawer and the route guard all read it, so a page can't be hidden in one
// place and reachable (or blank) in another. Locked pages are hidden from the
// nav (spec 7: hide by default); a direct URL shows an upgrade card instead.
// Reports stays for everyone — only its advanced tabs are gated, inside the
// page.
// ---------------------------------------------------------------------------

export interface RouteGate {
  /** The route and everything under it ("/projects" covers "/projects/:id"). */
  path: string;
  entitlement: EntitlementKey;
  /** How the upgrade card names the page: "<feature> comes with the Starter plan." */
  feature: string;
  /** "Decisions come…" rather than "Decisions comes…". */
  plural?: boolean;
}

export const ROUTE_GATES: readonly RouteGate[] = [
  // The leadership tools, each on its own entitlement so a plan can carry
  // any of them. Drivers covers Horizon (/drivers/horizon), the drivers and
  // KPIs over the year ahead. People history (interactions, person history)
  // has no web page yet. Tabs inside a page that a plan leaves out (Work's
  // Delegated, Review's transcripts and images, Insights' reports) are
  // decided by the page.
  { path: '/decisions', entitlement: 'decisions', feature: 'Decisions', plural: true },
  { path: '/drivers', entitlement: 'strategicDrivers', feature: 'Strategic drivers', plural: true },
  { path: '/focus/capacity', entitlement: 'capacityPlanning', feature: 'Capacity planning' },
  { path: '/assistant/profiles', entitlement: 'principalProfile', feature: 'Executive profiles', plural: true },
  { path: '/organisation/teams', entitlement: 'teams', feature: 'Teams', plural: true },
  { path: '/projects', entitlement: 'projects', feature: 'Projects', plural: true },
  { path: '/assistant/workspace', entitlement: 'eaWorkspace', feature: 'The EA workspace' },
];

export function routeGateFor(pathname: string): RouteGate | undefined {
  // React Router matches paths regardless of case, so the gate must too
  // (otherwise /Decisions renders the page the lock is meant to cover).
  const path = pathname.toLowerCase();
  return ROUTE_GATES.find(g => path === g.path || path.startsWith(`${g.path}/`));
}


export interface NavVisibility {
  isEaRole: boolean;
  isAdmin: boolean;
  has: (entitlement: EntitlementKey) => boolean;
  /** The user's tenant permissions (lib/permissionGates); absent = nothing restricted. */
  can?: (permission: PermissionKey) => boolean;
}

/** Whether a tenant nav item shows — role rules first, then the plan, then the user's permissions. */
export function isTenantNavItemVisible(path: string, v: NavVisibility): boolean {
  // The Assistant group is an assistant's own. Their home is Executives;
  // Today shows the executive they're acting for (web app v4 Phase 4.5).
  if ((path === '/assistant' || path.startsWith('/assistant/')) && !v.isEaRole) return false;
  // Organisation is for admins, Teams being one of its sections; anyone
  // else whose plan has teams gets Teams on its own.
  if (path === '/organisation' && !v.isAdmin) return false;
  if (path === '/organisation/teams' && v.isAdmin) return false;
  if (routeRoleFor(path) === 'admin' && !v.isAdmin) return false;
  const gate = routeGateFor(path);
  if (gate && !v.has(gate.entitlement)) return false;
  const needs = routePermissionFor(path);
  return !needs || !v.can || v.can(needs.permission);
}
